import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useNavContext } from "../context/NavContext";
import Nav from "./Nav";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import Spinner from "./Spinner";
import Breadcrumb from "./Breadcrumb";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";
import DataTable from "./DataTable";
import TablePagination from "./TablePagination";
import TableToolbar from "./TableToolbar";
import ModalFacturasClientes from "./ModalFacturasClientes";
import moment from "moment";
import "moment/locale/es";

const FacturasClientes = () => {
  const { proximasAVencer, proximosEventos, searchTerm, setSearchTerm } =
    useNavContext();

  useFunctionsPrevia();

  const [facturas, setFacturas] = useState([]);
  const [loadingFacturas, setLoadingFacturas] = useState(true);
  const [estadosCeldas, setEstadosCeldas] = useState({});
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [selectedItems, setSelectedItems] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "Fecha",
    direction: "desc",
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const direction =
        prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
  };

  const sortKeyMap = useMemo(
    () => ({
      Folio: "Folio",
      Fecha: "Fecha",
      Cliente: "receptor_Nombre",
      Total: "Total",
      Estado: "estado",
      Vence: "FechaVencimiento",
      Pagada: "TotalPagado",
    }),
    [],
  );

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem("columnPrefs_facturas");
    return saved
      ? JSON.parse(saved)
      : {
          Folio: true,
          Fecha: true,
          Cliente: true,
          Total: true,
          Estado: true,
          Vence: false,
          Pagada: false,
        };
  });

  const toggleEstado = useCallback(
    async (id) => {
      const ciclos = ["Pendiente", "Parcial", "Pagada", "Vencida"];
      const actual = estadosCeldas[id] || "Pendiente";
      const nuevo = ciclos[(ciclos.indexOf(actual) + 1) % ciclos.length];

      setEstadosCeldas((prev) => ({ ...prev, [id]: nuevo }));

      try {
        await updateDoc(doc(db, "facturasEmitidas", id), { estado: nuevo });
      } catch (e) {
        console.error("Error al actualizar estado:", e);
      }
    },
    [estadosCeldas],
  );

  const columnsFacturas = useMemo(() => {
    return [
      {
        id: "Folio",
        label: "Folio",
        visible: visibleColumns.Folio,
        sortKey: sortKeyMap.Folio,
        cellClassName: "table-td col-center",
        cellStyle: { fontWeight: "500", color: "#64748b" },
        render: (f) => f.Folio || "---",
      },
      {
        id: "Cliente",
        label: "Cliente",
        visible: visibleColumns.Cliente,
        sortKey: sortKeyMap.Cliente,
        cellClassName: "table-td col-left",
        render: (f) => (
          <div
            style={{ fontWeight: "400", color: "#1e293b", fontSize: "14px" }}
          >
            {f.receptor_Nombre}
          </div>
        ),
      },
      {
        id: "Fecha",
        label: "Fecha",
        visible: visibleColumns.Fecha,
        sortKey: sortKeyMap.Fecha,
        cellClassName: "table-td col-center",
        cellStyle: {
          color: "#64748b",
          textTransform: "lowercase",
          fontWeight: "400",
        },
        render: (f) =>
          f.Fecha
            ? moment(f.Fecha).format("D MMM YYYY").replace(".", "")
            : "---",
      },
      {
        id: "Vence",
        label: "Vencimiento",
        visible: visibleColumns.Vence,
        sortKey: sortKeyMap.Vence,
        cellClassName: "table-td col-center",
        cellStyle: { textTransform: "lowercase", color: "#64748b" },
        render: (f) =>
          f.FechaVencimiento
            ? moment(f.FechaVencimiento).format("D MMM YYYY").replace(".", "")
            : "---",
      },
      {
        id: "Total",
        label: "Total",
        visible: visibleColumns.Total,
        sortKey: sortKeyMap.Total,
        cellClassName: "table-td col-center",
        render: (f) => (
          <>
            <div
              style={{ fontWeight: "400", color: "#1e293b", fontSize: "14px" }}
            >
              {"$ " +
                new Intl.NumberFormat("es-MX", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(f.Total || 0)}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#94a3b8",
                fontWeight: "400",
                marginTop: "1px",
                textTransform: "uppercase",
              }}
            >
              {f.Moneda || "MXN"}
            </div>
          </>
        ),
      },
      {
        id: "Pagada",
        label: "Pagado",
        visible: visibleColumns.Pagada,
        sortKey: sortKeyMap.Pagada,
        cellClassName: "table-td col-center",
        render: (f) => (
          <span style={{ color: "#16a34a", fontWeight: "600" }}>
            {f.TotalPagado
              ? "$ " +
                new Intl.NumberFormat("es-MX", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(f.TotalPagado)
              : "$ 0.00"}
          </span>
        ),
      },
      {
        id: "Estado",
        label: "Estado",
        visible: visibleColumns.Estado,
        sortKey: sortKeyMap.Estado,
        cellClassName: "table-td col-center",
        stopPropagation: true,
        render: (f) => (
          <div
            className={`badge-status status-${
              estadosCeldas[f.id] === "Pagada"
                ? "aprobado"
                : estadosCeldas[f.id] === "Parcial"
                  ? "transito"
                  : estadosCeldas[f.id] === "Vencida"
                    ? "rechazado"
                    : "pendiente"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              toggleEstado(f.id);
            }}
          >
            {estadosCeldas[f.id]}
          </div>
        ),
      },
    ];
  }, [visibleColumns, sortKeyMap, estadosCeldas, toggleEstado]);

  useEffect(() => {
    localStorage.setItem(
      "columnPrefs_facturas",
      JSON.stringify(visibleColumns),
    );
  }, [visibleColumns]);

  const [filterStatus, setFilterStatus] = useState("Todos");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        showColumnConfig &&
        !e.target.closest(".column-config") &&
        !e.target.closest('[data-dd="columns"]')
      ) {
        setShowColumnConfig(false);
      }
      if (
        showExportDropdown &&
        !e.target.closest(".export-menu") &&
        !e.target.closest('[data-dd="export"]')
      ) {
        setShowExportDropdown(false);
      }
      if (
        showFilterDropdown &&
        !e.target.closest(".filter-panel") &&
        !e.target.closest('[data-tooltip-top="Filtros"]')
      ) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showColumnConfig, showExportDropdown, showFilterDropdown]);

  // ✅ SIN DATOS DE PRUEBA: solo firebase (para que puedas ver el empty state)
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "facturasEmitidas"),
      (snapshot) => {
        const firebaseData = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setFacturas(firebaseData);

        const states = {};
        firebaseData.forEach((f) => (states[f.id] = f.estado || "Pendiente"));
        setEstadosCeldas(states);

        setLoadingFacturas(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const filteredSortedData = useMemo(() => {
    let result = facturas.filter((f) => {
      const s = (searchTerm || "").toLowerCase();

      const folio = f.Folio?.toLowerCase() || "";
      const cliente = f.receptor_Nombre?.toLowerCase() || "";
      const rfc = f.receptor_Rfc?.toLowerCase() || "";
      const estado = estadosCeldas[f.id]?.toLowerCase() || "";

      const fechaDoc = f.Fecha
        ? moment(f.Fecha).format("D MMM YYYY").toLowerCase().replace(".", "")
        : "";
      const fechaVence = f.FechaVencimiento
        ? moment(f.FechaVencimiento)
            .format("D MMM YYYY")
            .toLowerCase()
            .replace(".", "")
        : "";

      const total = f.Total?.toString() || "";
      const pagado = f.TotalPagado?.toString() || "";

      const matchSearch =
        folio.includes(s) ||
        cliente.includes(s) ||
        rfc.includes(s) ||
        estado.includes(s) ||
        fechaDoc.includes(s) ||
        fechaVence.includes(s) ||
        total.includes(s) ||
        pagado.includes(s);

      const matchStatus =
        filterStatus === "Todos" || estadosCeldas[f.id] === filterStatus;

      return matchSearch && matchStatus;
    });

    return result.sort((a, b) => {
      const key = sortConfig.key;

      const getValue = (x) => {
        if (key === "estado") return estadosCeldas[x.id] || x.estado || "";
        if (key === "Fecha" || key === "FechaVencimiento") {
          const d = x[key];
          return d ? new Date(d).getTime() : 0;
        }
        const v = x[key];
        return v ?? "";
      };

      const A = getValue(a);
      const B = getValue(b);

      if (typeof A === "number" && typeof B === "number") {
        return sortConfig.direction === "asc" ? A - B : B - A;
      }

      const sA = ("" + A).toLowerCase();
      const sB = ("" + B).toLowerCase();
      return sortConfig.direction === "asc"
        ? sA.localeCompare(sB)
        : sB.localeCompare(sA);
    });
  }, [facturas, searchTerm, sortConfig, filterStatus, estadosCeldas]);

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) return;

    const esSingular = selectedItems.length === 1;
    const textoFactura = esSingular ? "factura" : "facturas";
    const tituloModal = esSingular ? "Eliminar Factura" : "Eliminar Facturas";
    const descripcion = esSingular
      ? "¿Estás seguro de que deseas eliminar esta factura?"
      : `¿Estás seguro de que deseas eliminar las ${selectedItems.length} facturas?`;

    const result = await Swal.fire({
      html: `
        <div class="custom-modal-container">
          <div class="custom-modal-header">
            <div class="custom-modal-icon-box">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" x2="12" y1="8" y2="12"></line>
                <line x1="12" x2="12.01" y1="16" y2="16"></line>
              </svg>
            </div>
            <h3 class="custom-modal-title">${tituloModal}</h3>
          </div>
          <p class="custom-modal-description">${descripcion}</p>
          <div class="custom-modal-actions">
            <button id="cancel-btn" class="btn-modal-cancel">Cancelar</button>
            <button id="confirm-btn" class="btn-modal-delete">Eliminar</button>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: false,
      buttonsStyling: false,
      allowOutsideClick: false,
      customClass: {
        popup: "swal-popup-reset",
        htmlContainer: "swal-html-reset",
      },
      didOpen: () => {
        document
          .getElementById("cancel-btn")
          .addEventListener("click", () => Swal.close());
        document
          .getElementById("confirm-btn")
          .addEventListener("click", () => Swal.clickConfirm());
      },
    });

    if (!result.isConfirmed) return;

    try {
      await Promise.all(
        selectedItems.map(async (id) => {
          const docRef = doc(db, "facturasEmitidas", id);
          await deleteDoc(docRef);
        }),
      );

      setSelectedItems([]);

      Swal.fire({
        icon: "success",
        title: "Completado",
        text: `${esSingular ? "La" : "Las"} ${textoFactura} ${
          esSingular ? "fue eliminada" : "fueron eliminadas"
        } correctamente.`,
        timer: 1500,
        showConfirmButton: false,
        customClass: { popup: "swal-popup-reset" },
      });
    } catch (error) {
      console.error(`Error al eliminar ${textoFactura}:`, error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `No se pudo eliminar: ${error.message}`,
        customClass: { popup: "swal-popup-reset" },
      });
    }
  };

  const currentItems = useMemo(() => {
    return filteredSortedData.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage,
    );
  }, [filteredSortedData, currentPage, itemsPerPage]);

  const toggleSelectAll = () => {
    if (selectedItems.length === currentItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(currentItems.map((item) => item.id));
    }
  };

  const totalPages = Math.ceil(filteredSortedData.length / itemsPerPage);

  return (
    <div className="no-sticky">
      <Nav
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />
      <Sidebar />

      <div className="main-content-wrapper">
        <div style={{ marginBottom: "25px" }}>
          <Breadcrumb />
          <h1 className="page-title">Facturas de Clientes</h1>
        </div>

        <TableToolbar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          searchPlaceholder="Buscar por folio o cliente..."
          showFilterDropdown={showFilterDropdown}
          setShowFilterDropdown={setShowFilterDropdown}
          showColumnConfig={showColumnConfig}
          setShowColumnConfig={setShowColumnConfig}
          showExportDropdown={showExportDropdown}
          setShowExportDropdown={setShowExportDropdown}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          filterOptions={["Todos", "Pagada", "Parcial", "Pendiente", "Vencida"]}
          onAdd={() => setModalOpen(true)}
          addButtonTooltip="Nueva Solicitud"
          fileInputRef={null}
          onExport={() => console.log("Exportando Facturas...")}
        />

        {selectedItems.length > 0 && (
          <div className="selection-bar">
            <span>
              {selectedItems.length === 1
                ? "1 seleccionado"
                : `${selectedItems.length} seleccionado(s)`}
            </span>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                className="btn-selection btn-edit"
                style={{ opacity: selectedItems.length > 1 ? 0.5 : 1 }}
                disabled={selectedItems.length > 1}
                onClick={() => {
                  const facturaId = selectedItems[0];
                  console.log("Editando factura ID:", facturaId);
                }}
              >
                <i className="bi bi-pencil-square" /> <span>Editar</span>
              </button>

              <button
                className="btn-selection btn-delete"
                onClick={handleDeleteSelected}
              >
                <i className="bi bi-trash" /> <span>Eliminar</span>
              </button>
            </div>
          </div>
        )}

        <DataTable
          loading={loadingFacturas}
          loadingFallback={
            <div style={{ padding: "50px" }}>
              <Spinner />
            </div>
          }
          rows={currentItems}
          columns={columnsFacturas}
          selectedIds={selectedItems}
          onToggleAll={toggleSelectAll}
          onToggleRow={(id) =>
            setSelectedItems((prev) =>
              prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
            )
          }
          sortConfig={sortConfig}
          onSort={handleSort}
          emptyTitle="Aún no hay facturas."
          emptySubtitle="Solicita facturas o importa un archivo para comenzar."
          emptyIcon={
            <img
              src="/img/no-data.svg"
              alt="Sin datos"
              style={{ width: 220, maxWidth: "70%", opacity: 0.95 }}
            />
          }
          footer={
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(p) => setCurrentPage(p)}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(n) => {
                setItemsPerPage(n);
                setCurrentPage(1);
              }}
              itemsPerPageOptions={[10, 25, 50]}
              selectedCount={selectedItems.length}
              selectedLabelSingular="factura"
              selectedLabelPlural="facturas"
              itemsLabel="Mostrar:"
            />
          }
        />
      </div>

      <ModalFacturasClientes
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSolicitudPendienteId={(id) => {
          console.log("Solicitud pendiente ID:", id);
        }}
      />
    </div>
  );
};

export default FacturasClientes;
